import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  RefreshCw, Search, Radio, Copy, ExternalLink, Eye, ChevronUp, ChevronDown,
  Wifi, WifiOff, Clock, Video, Settings, Trash2, Plus, X, Save, AlertCircle,
  Send, Terminal, ChevronRight
} from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import './LiveInputs.css'

function formatDate(dateStr) {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
  })
}

function shortId(id) {
  if (!id) return '—'
  return id.length > 16 ? id.slice(0, 8) + '...' + id.slice(-6) : id
}

// Build FFmpeg command for pushing a channel playlist to a CF live input
function buildFFmpegCommand(input, channelSlug = 'pihl-highlights', orientation = 'landscape') {
  if (!input?.rtmps) return null
  const rtmpsUrl = `${input.rtmps.url}${input.rtmps.streamKey}`
  const videoFilter = orientation === 'portrait'
    ? `-filter_complex "[0:v]scale=1080:1920,boxblur=20:5[bg];[0:v]scale=1080:608[fg];[bg][fg]overlay=(W-w)/2:(H-h)/2" \\`
    : `-c:v h264_videotoolbox -b:v 6000k \\`
  const bitrate = orientation === 'portrait' ? '4000k' : '6000k'
  if (orientation === 'portrait') {
    return `ffmpeg -re \\
  -i "https://play.tenband.tv/channel/${channelSlug}/twitter/video_720.m3u8" \\
  -use_wallclock_as_timestamps 1 \\
  -i "https://play.tenband.tv/channel/${channelSlug}/twitter/audio.m3u8" \\
  -map 0:v -map 1:a \\
  -filter_complex "[0:v]scale=1080:1920,boxblur=20:5[bg];[0:v]scale=1080:608[fg];[bg][fg]overlay=(W-w)/2:(H-h)/2" \\
  -c:v h264_videotoolbox -b:v 4000k \\
  -af "asetpts=N/SR/TB" \\
  -c:a aac -b:a 128k \\
  -f flv \\
  "${rtmpsUrl}"`
  }
  return `ffmpeg -re \\
  -i "https://play.tenband.tv/channel/${channelSlug}/twitter/video_720.m3u8" \\
  -use_wallclock_as_timestamps 1 \\
  -i "https://play.tenband.tv/channel/${channelSlug}/twitter/audio.m3u8" \\
  -map 0:v -map 1:a \\
  -c:v h264_videotoolbox -b:v 6000k \\
  -af "asetpts=N/SR/TB" \\
  -c:a aac -b:a 128k \\
  -f flv \\
  "${rtmpsUrl}"`
}

export default function LiveInputs() {
  const { api } = useAuth()

  const [inputs, setInputs] = useState([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  const [liveStatus, setLiveStatus] = useState({}) // uid -> { state, reason, lastSeen }
  const [searchTerm, setSearchTerm] = useState('')
  const [modeFilter, setModeFilter] = useState('')
  const [sortKey, setSortKey] = useState('created')
  const [sortDir, setSortDir] = useState('desc')
  const [selectedInput, setSelectedInput] = useState(null)
  const [showCreate, setShowCreate] = useState(false)
  const [newInput, setNewInput] = useState({ name: '', mode: 'automatic' })

  // Outputs state
  const [outputs, setOutputs] = useState([])
  const [outputsLoading, setOutputsLoading] = useState(false)
  const [showAddOutput, setShowAddOutput] = useState(false)
  const [newOutput, setNewOutput] = useState({ name: '', url: '', streamKey: '' })
  const [editingOutputName, setEditingOutputName] = useState(null)
  const [editingOutputNameVal, setEditingOutputNameVal] = useState('')
  const [revealedKeys, setRevealedKeys] = useState({})
  const [editingTitle, setEditingTitle] = useState(false)
  const [editingTitleVal, setEditingTitleVal] = useState('')
  const [outputStatus, setOutputStatus] = useState('')

  // FFmpeg dialog state
  const [showFFmpeg, setShowFFmpeg] = useState(false)
  const [ffmpegChannel, setFfmpegChannel] = useState('pihl-highlights')
  const [ffmpegCopied, setFfmpegCopied] = useState(false)
  const [ffmpegOrientation, setFfmpegOrientation] = useState('landscape')

  // Live connection state is a separate CF call (per-input status), so it loads
  // in the background and just annotates the table. Non-fatal if it fails.
  const loadLiveStatus = useCallback(async () => {
    try {
      const d = await api('/live/inputs/status')
      const map = {}
      for (const i of (d.inputs || [])) map[i.uid] = i
      setLiveStatus(map)
    } catch { /* leave the Live column blank */ }
  }, [api])

  const loadInputs = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api('/live-inputs')
      setInputs(data.liveInputs || [])
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setLoading(false)
    loadLiveStatus()
  }, [api, loadLiveStatus])

  useEffect(() => { loadInputs() }, [loadInputs])

  // Load outputs when an input is selected
  const loadOutputs = useCallback(async (uid) => {
    setOutputsLoading(true)
    setOutputs([])
    try {
      const data = await api(`/live-inputs/${uid}/outputs`)
      setOutputs(data.outputs || [])
    } catch (e) {
      setOutputStatus(`Error loading outputs: ${e.message}`)
    }
    setOutputsLoading(false)
  }, [api])

  useEffect(() => {
    if (selectedInput?.uid) loadOutputs(selectedInput.uid)
  }, [selectedInput?.uid, loadOutputs])

  function copyToClipboard(text, label) {
    navigator.clipboard.writeText(text)
    setStatus(`✅ Copied ${label || 'to clipboard'}`)
  }

  async function viewDetails(uid) {
    try {
      const data = await api(`/live-inputs/${uid}`)
      setSelectedInput(data)
      setShowAddOutput(false)
      setNewOutput({ name: '', url: '', streamKey: '' })
      setOutputStatus('')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function handleCreate() {
    if (!newInput.name.trim()) { setStatus('Name is required'); return }
    try {
      await api('/live-inputs', {
        method: 'POST',
        body: JSON.stringify({
          meta: { name: newInput.name },
          recording: { mode: newInput.mode }
        })
      })
      setStatus('✅ Live input created')
      setShowCreate(false)
      setNewInput({ name: '', mode: 'automatic' })
      loadInputs()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function handleDelete(uid, name) {
    if (!confirm(`Delete live input "${name}"? This cannot be undone.`)) return
    try {
      await api(`/live-inputs/${uid}`, { method: 'DELETE' })
      setStatus('✅ Live input deleted')
      if (selectedInput?.uid === uid) setSelectedInput(null)
      loadInputs()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function handleAddOutput() {
    if (!newOutput.name.trim() || !newOutput.url.trim() || !newOutput.streamKey.trim()) {
      setOutputStatus('Name, URL, and stream key are required'); return
    }
    try {
      await api(`/live-inputs/${selectedInput.uid}/outputs`, {
        method: 'POST',
        body: JSON.stringify({
          name: newOutput.name,
          streamKey: newOutput.streamKey,
          url: newOutput.url,
        })
      })
      setOutputStatus('✅ Output added')
      setShowAddOutput(false)
      setNewOutput({ name: '', url: '', streamKey: '' })
      loadOutputs(selectedInput.uid)
    } catch (e) {
      setOutputStatus(`Error: ${e.message}`)
    }
  }

  async function handleDeleteOutput(outputId, outputName) {
    if (!confirm(`Remove output "${outputName}"?`)) return
    try {
      await api(`/live-inputs/${selectedInput.uid}/outputs/${outputId}`, { method: 'DELETE' })
      setOutputStatus('✅ Output removed')
      loadOutputs(selectedInput.uid)
    } catch (e) {
      setOutputStatus(`Error: ${e.message}`)
    }
  }

  async function handleToggleOutput(outputId, currentEnabled) {
    try {
      await api(`/live-inputs/${selectedInput.uid}/outputs/${outputId}`, {
        method: 'PUT',
        body: JSON.stringify({ enabled: !currentEnabled }),
      })
      setOutputs(prev => prev.map(o => o.uid === outputId ? { ...o, enabled: !currentEnabled } : o))
      setOutputStatus(`Output ${!currentEnabled ? '✅ enabled' : '⏸ disabled'}`)
    } catch (e) {
      setOutputStatus(`Error: ${e.message}`)
    }
  }

  async function handleSaveTitle() {
    const name = editingTitleVal.trim()
    if (!name) return
    try {
      await api(`/live-inputs/${selectedInput.uid}`, {
        method: 'PUT',
        body: JSON.stringify({ meta: { name } }),
      })
      setSelectedInput(prev => ({ ...prev, meta: { ...prev.meta, name } }))
      setEditingTitle(false)
    } catch (e) {
      console.error('Failed to save title:', e)
    }
  }

  async function handleSaveOutputName(outputId) {
    const name = editingOutputNameVal.trim()
    if (!name) return
    try {
      await api(`/live-inputs/${selectedInput.uid}/outputs/${outputId}`, {
        method: 'PUT',
        body: JSON.stringify({ name }),
      })
      setOutputs(prev => prev.map(o => o.uid === outputId ? { ...o, name } : o))
      setEditingOutputName(null)
      setOutputStatus('✅ Output name updated')
    } catch (e) {
      setOutputStatus(`Error: ${e.message}`)
    }
  }

  function handleCopyFFmpeg() {
    const cmd = buildFFmpegCommand(selectedInput, ffmpegChannel, ffmpegOrientation)
    if (!cmd) return
    navigator.clipboard.writeText(cmd)
    setFfmpegCopied(true)
    setTimeout(() => setFfmpegCopied(false), 2000)
  }

  // Filter and sort
  const filtered = useMemo(() => {
    let list = inputs
    if (searchTerm) {
      const q = searchTerm.toLowerCase()
      list = list.filter(i =>
        (i.meta?.name || '').toLowerCase().includes(q) ||
        (i.uid || '').toLowerCase().includes(q)
      )
    }
    if (modeFilter) {
      list = list.filter(i => (i.recording?.mode || 'off') === modeFilter)
    }
    return list
  }, [inputs, searchTerm, modeFilter])

  function handleSort(key) {
    if (sortKey === key) {
      if (sortDir === 'asc') setSortDir('desc')
      else { setSortKey(null); setSortDir('asc') }
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const sorted = useMemo(() => {
    if (!sortKey) return filtered
    const s = [...filtered].sort((a, b) => {
      switch (sortKey) {
        case 'name': return (a.meta?.name || '').localeCompare(b.meta?.name || '')
        case 'created': return new Date(a.created || 0).getTime() - new Date(b.created || 0).getTime()
        case 'modified': return new Date(a.modified || 0).getTime() - new Date(b.modified || 0).getTime()
        case 'mode': return (a.recording?.mode || '').localeCompare(b.recording?.mode || '')
        default: return 0
      }
    }
    )
    return sortDir === 'desc' ? s.reverse() : s
  }, [filtered, sortKey, sortDir])

  const totalCount = inputs.length
  const autoCount = inputs.filter(i => i.recording?.mode === 'automatic').length
  const offCount = inputs.filter(i => !i.recording?.mode || i.recording?.mode === 'off').length
  const liveCount = Object.values(liveStatus).filter(s => s.state === 'connected').length

  function SortHeader({ label, sortId, className }) {
    const active = sortKey === sortId
    return (
      <th className={`${className || ''} th-sortable`} onClick={() => handleSort(sortId)}>
        <span className="th-sort-inner">
          {label}
          {active ? (
            sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
          ) : (
            <span className="th-sort-placeholder" />
          )}
        </span>
      </th>
    )
  }

  const ffmpegCmd = buildFFmpegCommand(selectedInput, ffmpegChannel, ffmpegOrientation)

  return (
    <div className="li-container">
      {/* Header */}
      <div className="li-header">
        <div className="li-header-left">
          <h1><Radio size={20} /> Live Inputs</h1>
          <div className="li-stats">
            <span className="li-stat">{totalCount} inputs</span>
            {liveCount > 0 && (
              <span className="li-stat li-stat-live"><span className="li-live-dot connected" /> {liveCount} live now</span>
            )}
            <span className="li-stat li-stat-auto"><Wifi size={11} /> {autoCount} recording</span>
            <span className="li-stat">{offCount} off</span>
          </div>
        </div>
        <div className="li-header-actions">
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={14} /> New Input
          </button>
          <button className="btn-ghost" onClick={loadInputs} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'spinning' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {status && (
        <div className={`li-status ${status.startsWith('Error') ? 'error' : ''}`}>
          {status}
          <button className="btn-ghost btn-sm" onClick={() => setStatus('')}>✕</button>
        </div>
      )}

      {/* Filters */}
      <div className="li-filters">
        <select value={modeFilter} onChange={e => setModeFilter(e.target.value)}>
          <option value="">All Modes</option>
          <option value="automatic">Automatic (recording)</option>
          <option value="off">Off</option>
        </select>
        <div className="li-search">
          <Search size={14} />
          <input
            type="text"
            placeholder="Search by name or ID..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="li-table-wrap">
        <table className="li-table">
          <thead>
            <tr>
              <SortHeader label="Name" sortId="name" />
              <th className="th-center th-live">Live</th>
              <th className="th-uid">Input ID</th>
              <SortHeader label="Mode" sortId="mode" className="th-center" />
              <SortHeader label="Created" sortId="created" />
              <SortHeader label="Modified" sortId="modified" />
              <th className="th-settings">Settings</th>
              <th className="th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="li-loading">Loading live inputs from Cloudflare...</td></tr>
            )}
            {!loading && sorted.length === 0 && (
              <tr><td colSpan={8} className="li-empty">
                {searchTerm ? 'No live inputs match your search' : 'No live inputs found'}
              </td></tr>
            )}
            {!loading && sorted.map(input => {
              const name = input.meta?.name || 'Unnamed'
              const mode = input.recording?.mode || 'off'
              const ls = liveStatus[input.uid]
              const lstate = ls?.state || 'unknown'
              const llabel = lstate === 'connected' ? 'live'
                : lstate === 'disconnected' ? 'offline'
                : lstate === 'no-ingest' ? 'idle'
                : lstate === 'unknown' ? '…' : lstate
              return (
                <tr key={input.uid} className="li-row">
                  <td className="td-name">
                    <div className="li-name-text">{name}</div>
                  </td>
                  <td className="td-live td-center">
                    <span
                      className={`li-live ${lstate}`}
                      title={ls ? `${lstate}${ls.reason ? ` (${ls.reason})` : ''}${ls.lastSeen ? ` · seen ${ls.lastSeen}` : ''}` : 'connection status'}
                    >
                      <span className={`li-live-dot ${lstate}`} />
                      {llabel}
                    </span>
                  </td>
                  <td className="td-uid">
                    <span className="li-uid-text" title={input.uid}>{shortId(input.uid)}</span>
                    <button
                      className="btn-ghost btn-xs"
                      onClick={() => copyToClipboard(input.uid, 'Input ID')}
                      title="Copy full ID"
                    >
                      <Copy size={11} />
                    </button>
                  </td>
                  <td className="td-mode">
                    <span className={`li-mode-badge ${mode}`}>
                      {mode === 'automatic' ? <Wifi size={11} /> : <WifiOff size={11} />}
                      {mode}
                    </span>
                  </td>
                  <td className="td-date">{formatDate(input.created)}</td>
                  <td className="td-date">{formatDate(input.modified)}</td>
                  <td className="td-settings">
                    {input.recording?.requireSignedURLs && (
                      <span className="li-setting-badge" title="Signed URLs required">🔒</span>
                    )}
                    {input.recording?.timeoutSeconds > 0 && (
                      <span className="li-setting-badge" title={`Timeout: ${input.recording.timeoutSeconds}s`}>
                        <Clock size={11} /> {input.recording.timeoutSeconds}s
                      </span>
                    )}
                    {input.deleteRecordingAfterDays && (
                      <span className="li-setting-badge" title={`Auto-delete after ${input.deleteRecordingAfterDays}d`}>
                        🗑 {input.deleteRecordingAfterDays}d
                      </span>
                    )}
                    {!input.recording?.requireSignedURLs && !input.recording?.timeoutSeconds && !input.deleteRecordingAfterDays && (
                      <span className="li-setting-none">—</span>
                    )}
                  </td>
                  <td className="td-actions">
                    <button
                      className="btn-ghost btn-sm"
                      onClick={() => viewDetails(input.uid)}
                      title="View details & credentials"
                    >
                      <Eye size={12} />
                    </button>
                    <button
                      className="btn-ghost btn-sm"
                      onClick={() => handleDelete(input.uid, name)}
                      title="Delete"
                    >
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Detail Panel */}
      {selectedInput && (
        <div className="modal-overlay" onClick={() => setSelectedInput(null)}>
          <div className="li-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              {editingTitle ? (
                <div className="li-title-edit">
                  <input
                    autoFocus
                    className="li-title-input"
                    value={editingTitleVal}
                    onChange={e => setEditingTitleVal(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleSaveTitle()
                      if (e.key === 'Escape') setEditingTitle(false)
                    }}
                  />
                  <button className="btn-primary btn-sm" onClick={handleSaveTitle}><Save size={12} /> Save</button>
                  <button className="btn-ghost btn-sm" onClick={() => setEditingTitle(false)}>Cancel</button>
                </div>
              ) : (
                <h2
                  className="li-title-editable"
                  onClick={() => { setEditingTitle(true); setEditingTitleVal(selectedInput.meta?.name || '') }}
                  title="Click to rename"
                >
                  <Radio size={16} /> {selectedInput.meta?.name || 'Live Input'}
                </h2>
              )}
              <button className="btn-ghost" onClick={() => setSelectedInput(null)}><X size={16} /></button>
            </div>

            <div className="li-detail-grid">
              {/* Input ID */}
              <div className="li-detail-section">
                <h3>Input ID</h3>
                <div className="li-detail-row copyable" onClick={() => copyToClipboard(selectedInput.uid, 'Input ID')}>
                  <code>{selectedInput.uid}</code>
                  <Copy size={12} />
                </div>
              </div>

              {/* RTMPS */}
              {selectedInput.rtmps && (
                <div className="li-detail-section">
                  <h3>RTMPS Ingest</h3>
                  <div className="li-detail-row copyable" onClick={() => copyToClipboard(selectedInput.rtmps.url, 'RTMPS URL')}>
                    <label>URL</label>
                    <code>{selectedInput.rtmps.url}</code>
                    <Copy size={12} />
                  </div>
                  <div className="li-detail-row copyable" onClick={() => copyToClipboard(selectedInput.rtmps.streamKey, 'Stream Key')}>
                    <label>Stream Key</label>
                    <code className="li-secret">{shortId(selectedInput.rtmps.streamKey)}</code>
                    <Copy size={12} />
                  </div>
                </div>
              )}

              {/* SRT */}
              {selectedInput.srt && (
                <div className="li-detail-section">
                  <h3>SRT</h3>
                  <div className="li-detail-row copyable" onClick={() => copyToClipboard(selectedInput.srt.url, 'SRT URL')}>
                    <label>URL</label>
                    <code>{selectedInput.srt.url}</code>
                    <Copy size={12} />
                  </div>
                  <div className="li-detail-row copyable" onClick={() => copyToClipboard(selectedInput.srt.streamId, 'Stream ID')}>
                    <label>Stream ID</label>
                    <code className="li-secret">{shortId(selectedInput.srt.streamId)}</code>
                    <Copy size={12} />
                  </div>
                </div>
              )}

              {/* Recording Settings */}
              <div className="li-detail-section">
                <h3>Recording Settings</h3>
                <div className="li-detail-kv">
                  <span>Mode:</span> <span>{selectedInput.recording?.mode || 'off'}</span>
                  <span>Signed URLs:</span> <span>{selectedInput.recording?.requireSignedURLs ? 'Yes' : 'No'}</span>
                  <span>Timeout:</span> <span>{selectedInput.recording?.timeoutSeconds || 0}s</span>
                  <span>Auto-delete:</span> <span>{selectedInput.deleteRecordingAfterDays ? `${selectedInput.deleteRecordingAfterDays} days` : 'Never'}</span>
                </div>
              </div>

              {/* Dates */}
              <div className="li-detail-section">
                <h3>Dates</h3>
                <div className="li-detail-kv">
                  <span>Created:</span> <span>{formatDate(selectedInput.created)}</span>
                  <span>Modified:</span> <span>{formatDate(selectedInput.modified)}</span>
                </div>
              </div>

              {/* ── OUTPUTS SECTION ── */}
              <div className="li-detail-section li-outputs-section">
                <div className="li-outputs-header">
                  <h3><Send size={13} /> Restream Outputs</h3>
                  <div className="li-outputs-actions">
                    <button className="btn-ghost btn-sm" onClick={() => setShowFFmpeg(v => !v)} title="FFmpeg push command">
                      <Terminal size={13} /> FFmpeg
                    </button>
                    <button className="btn-primary btn-sm" onClick={() => setShowAddOutput(v => !v)}>
                      <Plus size={12} /> Add Output
                    </button>
                  </div>
                </div>

                {outputStatus && (
                  <div className={`li-output-status ${outputStatus.startsWith('Error') ? 'error' : ''}`}>
                    {outputStatus}
                    <button className="btn-ghost btn-xs" onClick={() => setOutputStatus('')}>✕</button>
                  </div>
                )}

                {/* FFmpeg Dialog */}
                {showFFmpeg && (
                  <div className="li-ffmpeg-box">
                    <div className="li-ffmpeg-header">
                      <span><Terminal size={12} /> Push channel to this input via FFmpeg</span>
                      <button className="btn-ghost btn-xs" onClick={() => setShowFFmpeg(false)}><X size={12} /></button>
                    </div>
                    <div className="li-ffmpeg-channel-row">
                      <label>Channel slug:</label>
                      <input
                        value={ffmpegChannel}
                        onChange={e => setFfmpegChannel(e.target.value)}
                        placeholder="e.g. pihl-highlights"
                      />
                    </div>
                    <div className="li-ffmpeg-orientation-row">
                      <label>Format:</label>
                      <div className="li-ffmpeg-orient-toggle">
                        <button
                          className={`li-orient-btn ${ffmpegOrientation === 'landscape' ? 'active' : ''}`}
                          onClick={() => setFfmpegOrientation('landscape')}
                        >
                          ▬ Landscape 16:9
                        </button>
                        <button
                          className={`li-orient-btn ${ffmpegOrientation === 'portrait' ? 'active' : ''}`}
                          onClick={() => setFfmpegOrientation('portrait')}
                        >
                          ▮ Portrait 9:16
                        </button>
                      </div>
                      {ffmpegOrientation === 'portrait' && (
                        <span className="li-ffmpeg-orient-note">Blur background fill — for Instagram. Use a separate CF Live Input with Instagram as the output.</span>
                      )}
                    </div>
                    <pre className="li-ffmpeg-pre">{ffmpegCmd}</pre>
                    <div className="li-ffmpeg-footer">
                      <span className="li-ffmpeg-note">Cloudflare will restream to all configured outputs below.</span>
                      <button className="btn-primary btn-sm" onClick={handleCopyFFmpeg}>
                        <Copy size={12} /> {ffmpegCopied ? 'Copied!' : 'Copy Command'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Add Output Form */}
                {showAddOutput && (
                  <div className="li-add-output-form">
                    <div className="form-row">
                      <label>Name *</label>
                      <input
                        value={newOutput.name}
                        onChange={e => setNewOutput({ ...newOutput, name: e.target.value })}
                        placeholder="e.g. Twitter / X"
                      />
                    </div>
                    <div className="form-row">
                      <label>RTMP URL *</label>
                      <input
                        value={newOutput.url}
                        onChange={e => setNewOutput({ ...newOutput, url: e.target.value })}
                        placeholder="rtmp://va.pscp.tv:80/x/"
                      />
                    </div>
                    <div className="form-row">
                      <label>Stream Key *</label>
                      <input
                        value={newOutput.streamKey}
                        onChange={e => setNewOutput({ ...newOutput, streamKey: e.target.value })}
                        placeholder="your-stream-key"
                      />
                    </div>
                    <div className="li-add-output-actions">
                      <button className="btn-primary btn-sm" onClick={handleAddOutput}><Save size={12} /> Save Output</button>
                      <button className="btn-ghost btn-sm" onClick={() => { setShowAddOutput(false); setNewOutput({ name: '', url: '', streamKey: '' }) }}>Cancel</button>
                    </div>
                  </div>
                )}

                {/* Outputs List */}
                {outputsLoading && <div className="li-outputs-loading">Loading outputs...</div>}
                {!outputsLoading && outputs.length === 0 && !showAddOutput && (
                  <div className="li-outputs-empty">No restream outputs configured. Add one above.</div>
                )}
                {!outputsLoading && outputs.map(out => (
                  <div key={out.uid} className={`li-output-row ${out.enabled === false ? 'li-output-disabled' : ''}`}>
                    <button
                      className={`li-output-toggle ${out.enabled !== false ? 'on' : 'off'}`}
                      onClick={() => handleToggleOutput(out.uid, out.enabled !== false)}
                      title={out.enabled !== false ? 'Enabled — click to disable' : 'Disabled — click to enable'}
                    >
                      <span className="li-toggle-pip" />
                    </button>
                    <div className="li-output-info">
                      {editingOutputName === out.uid ? (
                        <div className="li-output-name-edit">
                          <input
                            autoFocus
                            value={editingOutputNameVal}
                            onChange={e => setEditingOutputNameVal(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') handleSaveOutputName(out.uid)
                              if (e.key === 'Escape') setEditingOutputName(null)
                            }}
                            className="li-output-name-input"
                          />
                          <button className="btn-ghost btn-xs" onClick={() => handleSaveOutputName(out.uid)} title="Save"><Save size={11} /></button>
                          <button className="btn-ghost btn-xs" onClick={() => setEditingOutputName(null)} title="Cancel"><X size={11} /></button>
                        </div>
                      ) : (
                        <span className="li-output-name">{out.name || 'Unnamed Output'}</span>
                      )}
                      <div className="li-output-field">
                        <span className="li-output-field-label">URL</span>
                        <span className="li-output-url">{out.url}</span>
                        <button className="btn-ghost btn-xs" onClick={() => copyToClipboard(out.url, 'URL')} title="Copy URL"><Copy size={10} /></button>
                      </div>
                      {out.streamKey && (
                        <div className="li-output-field">
                          <span className="li-output-field-label">Key</span>
                          <span className="li-output-url li-output-key">
                            {revealedKeys[out.uid] ? out.streamKey : '••••••••••••••••'}
                          </span>
                          <button className="btn-ghost btn-xs" onClick={() => setRevealedKeys(prev => ({ ...prev, [out.uid]: !prev[out.uid] }))} title={revealedKeys[out.uid] ? 'Hide' : 'Reveal'}>
                            <Eye size={10} />
                          </button>
                          <button className="btn-ghost btn-xs" onClick={() => copyToClipboard(out.streamKey, 'Stream Key')} title="Copy Key"><Copy size={10} /></button>
                        </div>
                      )}
                    </div>
                    <div className="li-output-row-actions">
                      <button
                        className="btn-ghost btn-xs"
                        onClick={() => { setEditingOutputName(out.uid); setEditingOutputNameVal(out.name || '') }}
                        title="Rename"
                      >
                        <Settings size={11} />
                      </button>
                      <button
                        className="btn-ghost btn-xs li-delete-output"
                        onClick={() => handleDeleteOutput(out.uid, out.name)}
                        title="Remove output"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="li-create-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Plus size={16} /> New Live Input</h2>
              <button className="btn-ghost" onClick={() => setShowCreate(false)}><X size={16} /></button>
            </div>
            <div className="li-create-form">
              <div className="form-row">
                <label>Name *</label>
                <input
                  value={newInput.name}
                  onChange={e => setNewInput({ ...newInput, name: e.target.value })}
                  placeholder="e.g. TBTV Main Stream"
                />
              </div>
              <div className="form-row">
                <label>Recording Mode</label>
                <select value={newInput.mode} onChange={e => setNewInput({ ...newInput, mode: e.target.value })}>
                  <option value="automatic">Automatic (record + playback)</option>
                  <option value="off">Off (live only, no recording)</option>
                </select>
              </div>
            </div>
            <div className="li-create-actions">
              <button className="btn-primary" onClick={handleCreate}><Plus size={14} /> Create</button>
              <button className="btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
